import { BudgetEstimateComponent } from '@/components/shared/budget-estimate/budget-estimate.component';
import {
  CustomFieldProps,
  DynamicField,
  DynamicForm,
  EditorFieldProps,
  FieldVariant,
  FileFieldProps,
  FileRepresentation,
  SelectFieldProps,
  SelectOption,
  TextFieldProps,
  DateFieldProps,
} from '../../../../components/form-builder/form-builder.types';
import { MultiFormAccordionComponent } from '../../../../components/multi-form-accordion/multi-form-accordion.component';
import { PpmRepository } from '../../../../store/ppm-state/ppm-state.repository';
import { FileRemoveEvent, FileSelectEvent } from 'primeng/fileupload';
import { Ppm, ResponseDoc } from '@/types';
import { map, Observable, of } from 'rxjs';
import { PpmMilestoneFormComponent } from '../../milestone-form/milestone-form.component';
import { MultiFormAccordionProps } from '@/components/multi-form-accordion/multiform-accordion.types';

interface getPpmUpdatePlanFormObjectProps {
  store: PpmRepository;
  acOptions: SelectOption[];
  clearDirection?: () => void;
  download?: (file: FileRepresentation) => void;
  openFile?: (file: FileRepresentation) => void;
  loading?: Observable<boolean>;
  isUpdatable?: Observable<boolean>;
  minDate?: Date;
}

export const getPpmUpdatePlanFormObject = ({
  store,
  acOptions,
  clearDirection,
  download,
  openFile,
  isUpdatable,
  minDate,
}: getPpmUpdatePlanFormObjectProps): DynamicForm => {
  const isDisabled = isUpdatable?.pipe(map(value => !value));

  const refField: DynamicField<TextFieldProps> = {
    label: 'ppm.form.name',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dto', 'ref'),
      onChange: (value: string) => {
        store.setNested('dto', 'ref', value);
      },
      placeholder: 'ppm.form.placeholders.sysName',
    },
  };

  const yearField: DynamicField<DateFieldProps> = {
    label: 'ppm.form.year',
    variant: FieldVariant.DATE,
    isRequired: true,
    isDisabled: of(true),
    error: store.getNestedObservable('errors', 'annee'),
    props: {
      value: store.getNestedObservable<number>('dto', 'annee'),
      onChange: () => undefined,
      view: 'year',
      format: 'yy',
    },
  };

  const acOptionsField: DynamicField<SelectFieldProps> = {
    label: 'ppm.form.ac_options',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isDisabled: of(true),
    error: store.getNestedObservable<string>('errors', 'acId'),
    props: {
      value: store.getObservable('acsSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('acsSelected', value);
        store.setNested('dto', 'acId', value?.code);
        store.clearError('acId');
        clearDirection?.();
      },
      placeholder: 'ppm.form.placeholders.ac_options',
      options: acOptions,
      filterEnabled: true,
    },
  };

  const milestoneField: DynamicField<CustomFieldProps> = {
    label: 'ppm.form.milestone',
    variant: FieldVariant.CUSTOM,
    props: {
      component: MultiFormAccordionComponent,
      injectKeys: ['accordionObject'],
      accordionObject: {
        titles: ['ppm.form.milestone2'],
        component: PpmMilestoneFormComponent,
        injectKeys: ['isDisabled', 'acId', 'minUpdateDate', 'actualizedCount'],
        isDisabled,
        disabledLevels: store
          .getObservable<Ppm>('response')
          .pipe(map(res => res?.realisations?.map((r, i) => (r.usedInDac ? i : -1)).filter(i => i !== -1) || [])),
        acId: store.getNestedObservable<number>('dto', 'acId'),
        actualizedCount: store.getNestedObservable<number>('response', 'nbrActualisation'),
        minUpdateDate: minDate,
        downSyndromeComponent: BudgetEstimateComponent,
        downSyndromeProps: {
          label: 'milestone.form.estimated_total_budget',
          value: store.getObservable<number>('totalEstimatedBudget'),
        },
      } satisfies MultiFormAccordionProps,
    },
  };

  const uploadField: DynamicField<FileFieldProps> = {
    label: 'ppm.form.files',
    variant: FieldVariant.FILE,
    isDisabled,
    props: {
      mode: 'advanced',
      accept: 'image/*, application/pdf',
      multiple: true,
      value: store.getNestedObservable<File[]>('dto', 'files'),
      onFileSelect: (event: FileSelectEvent) => {
        store.setNested('dto', 'ppmFiles', event.currentFiles);
      },
      onFileRemoved: (event: FileRemoveEvent) => {
        store.setNested(
          'dto',
          'ppmFiles',
          store.getNested<File[]>('dto', 'ppmFiles').filter(file => file.name !== event.file.name)
        );
      },
      files: store
        .getNestedObservable<ResponseDoc[]>('response', 'docs')
        .pipe(
          map(docs =>
            docs.map(doc => ({ id: doc.id, filename: doc.name, extension: doc.fileExtension, size: doc.fileSize, alfresco_id: doc.alfresco_id }))
          )
        ),
      onFileRepresentationChange: (file: ResponseDoc) => {
        store.setNested(
          'response',
          'docs',
          store.getNested<ResponseDoc[]>('response', 'docs').filter(doc => doc.id != file.id)
        );
        store.setNested(
          'dto',
          'docIds',
          store.getNested<number[]>('dto', 'docIds').filter(docId => docId !== file.id)
        );
      },
      onFileDownLoad: (file: FileRepresentation) => download?.(file),
      openFile: (file: FileRepresentation) => openFile?.(file),
    },
  };
  const notesField: DynamicField<EditorFieldProps> = {
    label: 'ppm.form.remarks',
    variant: FieldVariant.EDITOR,
    isDisabled,
    props: {
      height: '320px',
      value: store.getNestedObservable<string>('dto', 'remarque'),
      onChange: (value: string) => {
        store.setNested('dto', 'remarque', value);
        store.clearError('remarque');
      },
    },
  };

  return {
    title: 'Plan annuel des achats de cette année',
    description: 'Gérez et suivez votre plan annuel des achats pour cette année, Modifiez, mettez  à jour ou visualisez.',
    orientation: 'horizontal',
    isHeaderHidden: true,
    grids: [
      {
        title: 'Information Avis',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [acOptionsField],
          },
          {
            fields: [yearField, refField],
          },
        ],
      },
      {
        title: 'Réalisation envisagée',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [milestoneField],
          },
        ],
      },
      {
        title: 'Entrée secondaire',
        gridItems: [
          {
            fields: [uploadField],
          },
          {
            fields: [notesField],
          },
        ],
      },
    ],
  };
};
